import { z } from "zod";

export const personnelAccessSourceSchema = z.enum([
  "PRODUCTION",
  "TECHNICAL",
  "SHIFT_FORM_OPERATOR",
  "SHIFT_FORM_APPROVER",
  "LABEL",
]);
export type PersonnelAccessSource = z.infer<typeof personnelAccessSourceSchema>;

export const pinStatusSchema = z.enum([
  "NOT_CONFIGURED",
  "READY",
  "LOCKED",
  "LEGACY_UNREVEALABLE",
  "NOT_APPLICABLE",
]);
export type PinStatus = z.infer<typeof pinStatusSchema>;

export const labelRoleSchema = z.enum(["coordinator", "sales", "marketing", "quality", "printer"]);
export type LabelRole = z.infer<typeof labelRoleSchema>;

export const labelScopeSchema = z.object({
  customerCodes: z.array(z.string().trim().min(1).max(80)).max(100).nullable(),
  // The API resolves a displayed request number to its canonical UUID before
  // persistence. UUIDs remain accepted for an existing technical reference.
  requestIds: z.array(z.string().trim().min(1).max(120)).max(100).nullable(),
}).strict();
export type LabelScope = z.infer<typeof labelScopeSchema>;

export const personnelAccessEntrySchema = z.object({
  source: personnelAccessSourceSchema,
  sourcePersonId: z.string().min(1),
  name: z.string().min(1),
  active: z.boolean(),
  subtitle: z.string().nullable(),
  pinStatus: pinStatusSchema,
  pinChangedAt: z.string().datetime().nullable(),
  lockedUntil: z.string().datetime().nullable(),
  labelRoles: z.array(labelRoleSchema),
  labelScope: labelScopeSchema.nullable(),
  identityNote: z.string().nullable(),
});
export type PersonnelAccessEntry = z.infer<typeof personnelAccessEntrySchema>;

export const labelRequestOptionSchema = z.object({ id: z.string().uuid(), requestNo: z.string().trim().min(1) });
export type LabelRequestOption = z.infer<typeof labelRequestOptionSchema>;

export const personnelAccessListSchema = z.object({
  items: z.array(personnelAccessEntrySchema),
  labelRequestOptions: z.array(labelRequestOptionSchema),
});
export type PersonnelAccessList = z.infer<typeof personnelAccessListSchema>;

export const labelAccessUpdateSchema = z.object({
  roles: z.array(labelRoleSchema).min(1).max(5),
  scope: labelScopeSchema,
}).strict();
export type LabelAccessUpdate = z.infer<typeof labelAccessUpdateSchema>;

/** A label-only person has no corporate identity requirement. Email is used only
 * when the administrator deliberately requests PIN delivery. */
export const labelUserCreateSchema = z.object({
  fullName: z.string().trim().min(2).max(160),
  email: z.string().trim().email().max(320).nullable().optional(),
  roles: z.array(labelRoleSchema).min(1).max(5),
  scope: labelScopeSchema,
}).strict();
export type LabelUserCreate = z.infer<typeof labelUserCreateSchema>;

export const pinResultSchema = z.object({
  source: personnelAccessSourceSchema,
  sourcePersonId: z.string().min(1),
  name: z.string().min(1),
  pin: z.string().regex(/^\d{4,6}$/u),
  expiresAt: z.string().datetime(),
});
export type PinResult = z.infer<typeof pinResultSchema>;

export const pinGenerationRequestSchema = z.object({
  expectedUpdatedAt: z.string().datetime().nullable(),
}).strict();

export const personnelAccessHistorySchema = z.object({
  occurredAt: z.string().datetime(),
  action: z.string().min(1),
  outcome: z.enum(["SUCCESS", "DENIED", "FAILURE"]),
  actorName: z.string().nullable(),
});
export type PersonnelAccessHistory = z.infer<typeof personnelAccessHistorySchema>;
